import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import {
    ModalityHero, ProblemSection, HowItHelpsSection,
    ThePracticeSection, BeginTogetherSection,
    IssuesListSection, FAQSection,
} from "./modalityShared";

const issues = [
    "Anxiety and fear of the future",
    "Perfectionism and fear of failure",
    "People-pleasing and difficulty saying no",
    "Feeling disconnected from your values or sense of purpose",
    "Avoidance of difficult emotions or situations",
    "Chronic self-criticism and shame",
    "Life transitions and identity questions",
];

const faqs = [
    { q: "How is ACT different from other types of therapy?", a: "Many approaches focus on changing or challenging difficult thoughts. ACT focuses on changing your relationship to those thoughts so they have less power over your choices. You learn to notice what your mind is doing without being ruled by it." },
    { q: "Do I have to be in a lot of pain for ACT to help?", a: "Not at all. ACT is useful for anyone who feels stuck, avoidant, or disconnected from what matters to them — whether or not there is significant distress involved." },
    { q: "Can ACT be used alongside EMDR or Brainspotting?", a: "Yes. ACT is often integrated with trauma-focused approaches. While EMDR and Brainspotting work to process and release what has been stored in the nervous system, ACT provides the values-based framework and practical tools that help you move forward with intention." },
    { q: "How long does ACT take to feel helpful?", a: "Many people notice shifts in how they relate to their thoughts and feelings within a few sessions. Building lasting psychological flexibility takes more time, but the tools are ones you can use immediately and carry with you long after therapy ends." },
];

const steps = [
    { n: "I", title: "Exploration and Values Clarification", body: "In your first sessions, we slow down to understand what is keeping you stuck and what you actually want your life to look like. We identify your values — not as abstract ideals, but as practical guides for how you want to live and show up." },
    { n: "II", title: "Building Psychological Flexibility", body: "We work on your relationship with difficult thoughts, emotions, and sensations — learning to observe them without being controlled by them. This is where the core ACT skills take root." },
    { n: "III", title: "Taking Meaningful Action", body: "With a clearer sense of what matters and greater flexibility in how you respond, we focus on helping you take real steps forward. Sessions become about practice, momentum, and sustaining the changes you are building." },
];

const phrases = [
    "Fear is not the enemy. Letting fear make your decisions is.",
    "You do not have to feel ready. You just have to be willing.",
    "Clarity about what matters makes the hard steps worth taking.",
    "ACT is not about fixing yourself. It is about choosing yourself.",
];

export default function ACTPage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="ACT Therapy in Wisconsin & Colorado | Alchemy Practice"
                description="Acceptance and Commitment Therapy for anxiety, overthinking, and fear of moving forward. Virtual sessions in Wisconsin and Colorado. Alchemy Practice."
            />
            <Nav />
            <RevealOnScroll>
                <ModalityHero
                    testidPrefix="act"
                    eyebrow="Acceptance & Commitment Therapy"
                    headline="ACT Therapy for Anxiety, Overthinking, and"
                    italic="Fear of Moving Forward"
                    subhead="Fear has slowly become the thing organizing your life."
                />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <ProblemSection
                    testidPrefix="act"
                    eyebrow="What Brings You Here"
                    headline="You Keep Waiting Until"
                    italic="You Feel Ready"
                    firstLine="You tell yourself that once you feel more confident, more certain, less anxious, or less afraid, then you will finally begin living the life you want."
                    paragraphs={[
                        "But the waiting continues.",
                        "You overthink decisions. You avoid risks. You hesitate in relationships, opportunities, and goals that matter deeply to you.",
                        "Over time, anxiety quietly becomes the thing deciding what feels possible for your life.",
                    ]}
                />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <HowItHelpsSection
                    testidPrefix="act"
                    eyebrow="The Approach"
                    headline="How ACT"
                    italic="Helps"
                    paragraphs={[
                        "Acceptance and Commitment Therapy helps you stop organizing your life around fear.",
                        "Rather than waiting until anxiety disappears, ACT helps you learn how to move toward what matters even when fear is still present.",
                        "Over time, many clients feel more intentional, grounded, and aligned with the life they want to create.",
                    ]}
                    quote={'"What I love about ACT is how deeply it reconnects people with themselves. So many people have spent years making decisions based on anxiety, perfectionism, or avoiding discomfort that they lose touch with what they actually want. ACT helps clarify your values and build the psychological flexibility needed to begin making decisions from that place instead."'}
                />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <IssuesListSection
                    testidPrefix="act"
                    eyebrow="What ACT Helps With"
                    headline="When fear has become the"
                    italic="default setting."
                    items={issues}
                />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <FAQSection
                    testidPrefix="act"
                    heading="Questions About"
                    italic="ACT"
                    faqs={faqs}
                />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ThePracticeSection
                    testidPrefix="act"
                    eyebrow="At Alchemy Practice"
                    headline="Therapy That Evolves"
                    italic="With You"
                    paragraphs={[
                        "ACT at Alchemy Practice is not about forcing positivity or \"fixing\" emotions. It is about helping you build a different relationship with fear so it no longer quietly controls your life.",
                        "As therapy progresses, many clients naturally shift from weekly sessions into more spacious support focused on maintaining alignment, growth, and self-trust over time.",
                    ]}
                />
                <SectionDivider color="gold" bg="#F2EDE6" />
                <BeginTogetherSection steps={steps} testidPrefix="act" />
                <Marquee phrases={phrases} />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
